import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { CustomerAction, CustomerActions } from "@app/customer/store/actions";
import { emailValid, firstnameEmpty, humanNameValid, lastnameEmpty } from "@app/shared/validators";
import { NgxPermissionsObject, NgxPermissionsService } from "ngx-permissions";
import { Observable, Subject, of, takeUntil } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map, take, tap } from "rxjs/operators";

import { ActivatedRoute } from "@angular/router";
import { AuditLogInput, createAuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { Customer } from "@app/customer/model/customer";
import { CustomerService } from "@app/customer/services/customer.service";
import { Entity } from "@app/entity/model/entity";
import { EntityWithAncestors } from "@app/entity/services/entity-db.service";
import { EntityService } from "@app/entity/services/entity.service";
import { RouterGo } from "@app/root-store/actions";
import { NgChanges } from "@app/shared/model/ng-changes.model";
import { AppErrorStateMatcher } from "@app/shared/validators/app-error-state-matcher.service";
import { phoneValid } from "@app/shared/validators/phoneValid";
import { User } from "@app/user/model/user";
import { ROLE_NAMES, UserRole } from "@app/user/model/user-role";

@Component({
    selector: "app-customer-edit",
    templateUrl: "./customer-edit.component.html",
    styleUrls: ["./customer-edit.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerEditComponent implements OnInit, OnDestroy, OnChanges {
    @Input() isStandalone = false;
    @Input() existedCustomer: Customer = null;

    // set for when editing customer in modal window
    @Input() removeAction: "remove" | "delete" | "" = "delete";
    @Input() hideCancelBtn = false;
    @Input() editCustomerPage = false;
    phoneTypeList = ["Home", "Cell", "Work"];
    showPhone2 = false;
    showPhone3 = false;

    entities$: Observable<EntityWithAncestors[]>;
    userRoles$: Observable<UserRole[]>;
    customer$: Observable<Customer>;
    customerForm: FormGroup = this.formBuilder.group({
        id: ["", Validators.required],
        firstName: ["", [firstnameEmpty, humanNameValid]],
        lastName: ["", [lastnameEmpty, humanNameValid]],
        email: ["", emailValid],
        phone: ["", phoneValid],
        phone2: ["", phoneValid],
        phone3: ["", phoneValid],
        phoneType: [""],
        phone2Type: [""],
        phone3Type: [""],
        entityId: ["", Validators.required],
        roleId: ["", Validators.required],
        gitkitId: [""],
    });
    submitAttempted = false;
    isUserLoading = false;

    private unsubscribe$ = new Subject();
    private entities: Entity[] = [];
    private _customer: Customer = null;

    // Overridden by display-customer-info-component
    @Output()
    emitter = new EventEmitter<CustomerActions | RouterGo>();

    constructor(
        public errorMatcher: AppErrorStateMatcher,
        private activatedRoute: ActivatedRoute,
        private readonly formBuilder: FormBuilder,
        private readonly customerService: CustomerService,
        private readonly permissionsService: NgxPermissionsService,
        private readonly entityService: EntityService
    ) {}

    ngOnChanges(changes: NgChanges<CustomerEditComponent>): void {
        if (changes.existedCustomer?.currentValue) {
            this.setCustomer(changes.existedCustomer.currentValue);
        }
    }

    ngOnInit() {
        this.activatedRoute.data.pipe(take(1)).subscribe(({ isStandalone }) => {
            this.isStandalone = isStandalone;
        });
        this.entities$ = this.entityService.selectEntitiesArray$().pipe(
            tap(entities => (this.entities = entities)),
            takeUntil(this.unsubscribe$)
        );
        this.userRoles$ = this.customerService.dispatchAndSelectUserRoles().pipe(
            takeUntil(this.unsubscribe$),
            map(roles => this.filterRoles(roles)),
            // Fix ExpressionChanged errors
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
        );

        this.checkToLoadAllEntityUsers();

        this.customerService.subscribeToEmitter(this.emitter);

        this.setCustomer(this.existedCustomer);

        this.customerForm.controls.email.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(value => {
            this.customerForm.controls.email.setValue(value?.toLowerCase());
        });
    }

    updateCustomerForm() {
        return (customer: Customer) => {
            if (customer.gitkitId) {
                this.customerForm.controls.email.disable();
            }
            if (customer) {
                setTimeout(() => {
                    // expression change error without timeout
                    const roleId = customer.roleId + "";
                    this.customerForm.patchValue({
                        ...customer,
                        roleId,
                    });
                }, 0);
                this.updatePhoneTypes(customer);
                this.showPhone2 = !!customer?.phone2;
                this.showPhone3 = !!customer?.phone3;
            }
        };
    }

    private setCustomer(customer) {
        this.isUserLoading = true;
        this.customer$ = this.getCustomer$(customer);

        this.customer$.pipe(takeUntil(this.unsubscribe$)).subscribe(customer => {
            this.updateCustomerForm()(customer);
            this._customer = customer;
            this.isUserLoading = false;
        });
    }

    private getCustomer$(customer: Customer) {
        if (customer?.id) {
            return of(customer);
        }

        if (this.customer$) {
            return this.customer$.pipe(take(1));
        }

        return this.customerService.selectCurrentCustomer().pipe(
            filter(customer => !!customer),
            take(1)
        );
    }

    get fields() {
        return this.customerForm.controls;
    }

    get isSaveDisabled() {
        return this.submitAttempted || !this.customerForm.valid;
    }

    stripPhoneFormat(phone: string) {
        return (phone || "").replace(/[()-. ]/g, "");
    }
    updatePhoneTypes(customer: Customer) {
        if (customer.phoneType) {
            this.customerForm.controls.phoneType.setValue(
                customer.phoneType.replace(/^[a-z]/, customer.phoneType.slice(0, 1).toUpperCase())
            );
        }
        if (customer.phone2Type) {
            this.customerForm.controls.phone2Type.setValue(
                customer.phone2Type.replace(/^[a-z]/, customer.phone2Type.slice(0, 1).toUpperCase())
            );
        }
        if (customer.phone3Type) {
            this.customerForm.controls.phone3Type.setValue(
                customer.phone3Type.replace(/^[a-z]/, customer.phone3Type.slice(0, 1).toUpperCase())
            );
        }
    }

    saveCustomer() {
        this.customerForm.patchValue(
            {
                phone: this.stripPhoneFormat(this.customerForm.controls.phone.value),
                phone2: this.stripPhoneFormat(this.customerForm.controls.phone2.value),
                phone3: this.stripPhoneFormat(this.customerForm.controls.phone3.value),
            },
            { onlySelf: true, emitEvent: false }
        );

        this.submitAttempted = true;
        const subscription = this.emitter.subscribe(() => {
            this.submitAttempted = false;
            subscription.unsubscribe();
        });
        const updatedCustomer = this.customerForm.getRawValue();
        const logInput: AuditLogInput = this.getAuditLogInput(updatedCustomer, this._customer);

        this.customerService.upsertCustomer(this.customerForm, this.emitter, null, logInput);
    }

    private getAuditLogInput(newCustomer, oldCustomer): AuditLogInput {
        const newName = `${newCustomer.firstName} ${newCustomer.lastName}`;
        const newEntity = this.entities.find(e => e.id === +newCustomer.entityId);
        let data = `Customer Name: ${newName}<br/>Email: ${newCustomer.email}<br/>Company: ${newEntity?.name}`;
        if (oldCustomer?.id) {
            const oldName = `${oldCustomer.firstName} ${oldCustomer.lastName}`;
            const oldEntity = this.entities.find(e => e.id === oldCustomer.entityId);
            data = `Original Name: ${oldName}<br/>Email: ${oldCustomer?.email}<br/>Company: ${oldEntity?.name}<br/>
                New Name: ${newName}<br/>Email: ${newCustomer?.email}<br/>Company: ${newEntity?.name}`;
        }
        const logKey = createAuditLogKey(LOG_CATEGORY.COMPANY, LOG_TYPE.CUSTOMER, oldCustomer?.id ? LOG_SUB_TYPE.MODIFY : LOG_SUB_TYPE.ADD);

        return createAuditLogInput(logKey, data, {
            id: null,
            entityId: newEntity.id,
        });
    }

    removeCustomer(customer: Customer) {
        const logData = this.getLogData(customer);

        this.emitter.emit({ type: "Remove", customer, logData } as CustomerAction);
    }

    deleteCustomer(customer: Customer) {
        const logData = this.getLogData(customer);
        this.customerService.deleteAction(customer, this.emitter, logData);
    }
    addMorePhone() {
        if (!this.showPhone2) {
            this.showPhone2 = true;
            return;
        }
        if (!this.showPhone3) {
            this.showPhone3 = true;
        }
    }
    private getLogData(customer: User) {
        const newEntity = this.entities.find(e => e.id === customer?.entityId);
        const newName = `${customer.firstName} ${customer.lastName}`;
        const logKey = createAuditLogKey(LOG_CATEGORY.COMPANY, LOG_TYPE.CUSTOMER, LOG_SUB_TYPE.DELETE);
        const data = `Customer Name: ${newName}<br/>Email: ${customer.email}<br/>Company: ${newEntity?.name}`;

        return createAuditLogInput(logKey, data, {
            id: null,
            entityId: customer.id,
        });
    }

    cancel() {
        const hasSubscribers = !!this.emitter.observers.length;
        const action = this.customerService.cancelAction(!hasSubscribers);
        this.emitter.emit(action);
    }

    resendInvite(customer: Customer) {
        this.customerService.resendInvite(customer);
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    /**
     * if the logged-in user is able to view all customers, then fetch all
     * most of use is for Clare Admin
     */
    private checkToLoadAllEntityUsers() {
        this.permissionsService.permissions$
            .pipe(
                takeUntil(this.unsubscribe$),
                tap((permissions: NgxPermissionsObject) => {
                    const viewCustomerList = permissions["viewCustomerList"];

                    if (viewCustomerList) {
                        this.customerService.dispatchAllUsers();
                    }
                })
            )
            .subscribe();
    }
    private filterRoles(roles: UserRole[]) {
        if (this.editCustomerPage) {
            return roles.filter(r => r.name !== ROLE_NAMES.CLARE_ADMIN);
        }
        return roles;
    }
}
